import { MASKED_SECRET_INDICATOR } from "./library-auth-utils"

/**
 * The client-side half of the one rule for secret-class admin fields (spec R6.3,
 * #3050 slice 0b). The backend (`core/secret_ref.py`) stores only a Key Vault
 * *reference* — `@kv:<name>` — and answers anything else, a bare name included,
 * with a 400. The cards still ask the admin for the NAME, because that is what an
 * admin reads off the vault, so the `@kv:` prefix is added here instead of being
 * typed. Empty stays empty (the backend clears), and the masked sentinel the cards
 * seed from a stored secret is passed through untouched (the backend keeps).
 */

/** Prefix the backend requires on a stored secret-class field. */
export const SECRET_REF_PREFIX = "@kv:"

/** Azure Key Vault's own upper bound on a secret name (backend `SECRET_REF_NAME_MAX_LENGTH`). */
export const SECRET_REF_NAME_MAX_LENGTH = 127

/**
 * Key Vault secret names are letters, digits and hyphens, 1-127 characters, and must
 * carry at least one alphanumeric — `-` and `---` name nothing. This is the backend's
 * `SECRET_REF_NAME_PATTERN` spelled for JS; the two are meant to be read side by side,
 * because a client that accepts more than the server does turns a typo into a 400
 * after the save instead of a message under the field.
 *
 * Shape only. Whether the name exists in the deployment's reference catalogue (the
 * second half of spec R6.3, sourced per R6.4/R6.4a) is checked in its own slice and
 * deliberately not here — neither side pretends to answer it.
 */
export const SECRET_REF_NAME_PATTERN = /^(?=.{1,127}$)[a-zA-Z0-9-]*[a-zA-Z0-9][a-zA-Z0-9-]*$/

/**
 * The bare secret name inside what the admin typed: surrounding whitespace and an
 * already-typed `@kv:` prefix removed, so the prefix is never doubled.
 */
export function secretRefName(value: string): string {
  const trimmed = value.trim()
  return trimmed.startsWith(SECRET_REF_PREFIX)
    ? trimmed.slice(SECRET_REF_PREFIX.length).trim()
    : trimmed
}

/**
 * Whether the field may be submitted. An empty box (clear) and the masked sentinel
 * (keep the stored reference) are always valid; anything else has to name a Key
 * Vault secret the way the backend validator spells it.
 */
export function isValidSecretRefInput(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed || trimmed === MASKED_SECRET_INDICATOR) return true
  return SECRET_REF_NAME_PATTERN.test(secretRefName(trimmed))
}

/**
 * What to send for a secret-class field: `null` clears, the masked sentinel keeps
 * the stored reference, and a typed name becomes `@kv:<name>`. Only call it once
 * {@link isValidSecretRefInput} holds — an invalid name would otherwise be sent on
 * to be refused with a 400.
 */
export function toSecretRef(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  if (trimmed === MASKED_SECRET_INDICATOR) return trimmed
  return `${SECRET_REF_PREFIX}${secretRefName(trimmed)}`
}
